import { Button, Input, message, Select } from "antd";
import Title from "antd/es/typography/Title";
import React, { FC, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { UserItem, useXlsExportMaterialItemsMutation } from "shared/api";
import {
  useDebouncedValue,
  useItemViewMaterialCategoryData,
} from "shared/hooks";
import { AccessPoints } from "shared/routers";
import { ActiveDynamicTrackerFieldFilter } from "shared/types";
import { Loader, Page } from "shared/ui";
import { ItemViewTable } from "./ui";

interface ItemViewProps {
  user?: UserItem;
}

const ItemView: FC<ItemViewProps> = ({ user }) => {
  const { t } = useTranslation("", { keyPrefix: "itemView" });

  const hasEditPermission = user?.access_points?.includes(
    AccessPoints.ITEMS_EDIT
  );

  const [visibleIds, setVisibleIds] = useState<number[]>([]);
  const [globalSerialNumber, setGlobalSerialNumber] = useState("");
  const [xlsExport, { isLoading: isExporting }] =
    useXlsExportMaterialItemsMutation();

  const handleGlobalSerialNumberChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setGlobalSerialNumber(e.target.value);
  };

  const {
    isLoadingMaterialCategories,
    adaptedMaterialCategories,
    handleSelectMaterialCategory,
    selectedMaterialCategoryItem,
  } = useItemViewMaterialCategoryData();

  // Dynamic tracker field filters keyed by `${section}.${field}`. The backend
  // accepts multiple filters joined by ";;", so each column owns its own entry.
  const [dynamicFilters, setDynamicFilters] = useState<
    Record<string, ActiveDynamicTrackerFieldFilter>
  >({});

  const dynamicTrackerFieldsFilter = useMemo(() => {
    const parts = Object.values(dynamicFilters).map(
      (filter) =>
        `${filter.section},${filter.field},${filter.operation},${filter.value}`
    );
    return parts.length ? parts.join(";;") : undefined;
  }, [dynamicFilters]);

  const debouncedDynamicTrackerFieldsFilter = useDebouncedValue(
    dynamicTrackerFieldsFilter,
    400
  );

  const applyDynamicFilter = (filter: ActiveDynamicTrackerFieldFilter) => {
    setDynamicFilters((prev) => ({
      ...prev,
      [`${filter.section}.${filter.field}`]: filter,
    }));
  };

  const clearDynamicFilter = (section: string, field: string) => {
    setDynamicFilters((prev) => {
      const next = { ...prev };
      delete next[`${section}.${field}`];
      return next;
    });
  };

  const clearDynamicTrackerFieldsFilter = () => setDynamicFilters({});

  const handleExportAllXLS = async (allIds: number[]) => {
    if (!allIds?.length) return;

    try {
      const blob = await xlsExport({ ids: allIds }).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");

      const categoryName =
        selectedMaterialCategoryItem?.name?.replace(/\s+/g, "_") || "All";

      a.href = url;
      a.download = `export_${categoryName}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      message.error("Export error");
    }
  };

  const selectedMaterialItem = adaptedMaterialCategories.find(
    (cat) => cat.value === selectedMaterialCategoryItem?.id
  );

  return (
    <Page title={t("title")}>
      {isLoadingMaterialCategories && <Loader type="fullscreen-backdrop" />}

      <div className="flex items-end justify-between mb-4">
        <div className="flex items-center gap-5">
          <div>
            <Title level={5}>{t("selectMaterialCategory")}</Title>

            <Select
              options={adaptedMaterialCategories}
              className="w-full"
              placeholder={t("selectMaterialCategory")}
              onChange={handleSelectMaterialCategory}
              value={selectedMaterialCategoryItem?.id || null}
            />
          </div>
          <div>
            <Title level={5}>{t("selectSerialNumber")}</Title>
            <Input
              className="w-full"
              placeholder={t("selectSerialNumber") || ""}
              value={globalSerialNumber}
              onChange={handleGlobalSerialNumberChange}
            />
          </div>
        </div>
        <Button
          type="primary"
          loading={isExporting}
          onClick={() => handleExportAllXLS(visibleIds)}
          className="flex items-center gap-2"
          style={{
            backgroundColor: "#217346",
            borderColor: "#217346",
          }}
          disabled={!visibleIds.length}
        >
          <img
            src="/xls-icon.png"
            alt="xls-icon"
            style={{ width: 22, height: 22, objectFit: "contain" }}
          />
          Export{" "}
          {selectedMaterialItem?.label ? selectedMaterialItem?.label : "All"}
        </Button>
      </div>

      <ItemViewTable
        hasEditPermission={hasEditPermission}
        globalSerialNumber={globalSerialNumber}
        selectedMaterialCategoryItem={selectedMaterialCategoryItem ?? null}
        setGlobalSerialNumber={setGlobalSerialNumber}
        handleSelectMaterialCategory={handleSelectMaterialCategory}
        onExportAll={setVisibleIds}
        dynamicTrackerFieldsFilter={debouncedDynamicTrackerFieldsFilter}
        clearDynamicTrackerFieldsFilter={clearDynamicTrackerFieldsFilter}
        dynamicFilters={dynamicFilters}
        applyDynamicFilter={applyDynamicFilter}
        clearDynamicFilter={clearDynamicFilter}
      />
    </Page>
  );
};

export { ItemView };
